// src/modulos/Parametros/ProductCategoryPage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const PRODUCTS_COLLECTION = 'products';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const ITEMS_PER_PAGE = 10;

const ProductCategoryPage = () => {
    const can = usePagePermissions('categoria_de_produtos');

    // Estados Locais
    const [editingCategory, setEditingCategory] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [markupPadrao, setMarkupPadrao] = useState('');
    const [markupMinimo, setMarkupMinimo] = useState('');
    const [order, setOrder] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('order');
    const [sortDirection, setSortDirection] = useState('asc');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [dependencyError, setDependencyError] = useState('');

    // Hooks de Dados
    const {
        data: categories,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(CATEGORY_COLLECTION, 'order');

    const { data: products } = useFirestore(PRODUCTS_COLLECTION);
    const { data: rawMaterials } = useFirestore(RAW_MATERIALS_COLLECTION);

    // ============================================================
    // 1. DEFINIR filteredAndSortedCategories ANTES dos callbacks
    // ============================================================
    const filteredAndSortedCategories = useMemo(() => {
        let result = [...(categories || [])];

        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            result = result.filter(category =>
                category.name.toLowerCase().includes(lowerCaseSearch) ||
                category.markupPadrao?.toString().includes(searchTerm) ||
                category.markupMinimo?.toString().includes(searchTerm) ||
                category.order?.toString().includes(searchTerm)
            );
        }

        result.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            if (sortField === 'markupPadrao' || sortField === 'markupMinimo') {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
            } else if (sortField === 'order') {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
            } else if (sortField === 'name') {
                aVal = (aVal || '').toLowerCase();
                bVal = (bVal || '').toLowerCase();
            }

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [categories, searchTerm, sortField, sortDirection]);

    const totalFilteredItems = filteredAndSortedCategories.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentCategories = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredAndSortedCategories.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredAndSortedCategories, currentPage]);

    // Verificar dependências
    const hasDependencies = useCallback((categoryId) => {
        const hasProducts = products?.some(product => product.categoryId === categoryId) || false;
        const hasRawMaterials = rawMaterials?.some(material => material.categoryId === categoryId) || false;
        return { hasProducts, hasRawMaterials, hasAny: hasProducts || hasRawMaterials };
    }, [products, rawMaterials]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingCategory(null);
        setNameInput('');
        setMarkupPadrao('');
        setMarkupMinimo('');
        setOrder('');
        setFormError('');
        setFormSuccess('');
        setDependencyError('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((category) => {
        setEditingCategory(category);
        setNameInput(category.name);
        setMarkupPadrao(category.markupPadrao?.toString() || '');
        setMarkupMinimo(category.markupMinimo?.toString() || '');
        setOrder(category.order?.toString() || '');
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
        setDependencyError('');
    }, []);

    // Confirmar Delete (AGORA filteredAndSortedCategories já está definido)
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const dependencies = hasDependencies(id);

            if (dependencies.hasAny) {
                let errorMsg = `Não é possível excluir a categoria "${name}" pois existem itens vinculados:\n`;
                if (dependencies.hasProducts) errorMsg += `\n- Produtos cadastrados nesta categoria`;
                if (dependencies.hasRawMaterials) errorMsg += `\n- Matérias-primas vinculadas a esta categoria`;
                errorMsg += `\n\nRemova ou reassocie estes itens antes de excluir a categoria.`;
                setDependencyError(errorMsg);
                setDeleteLoading(false);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Categoria "${name}" excluída com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                // Usar filteredAndSortedCategories (já definido acima)
                const remainingItems = filteredAndSortedCategories.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setDependencyError(`Erro ao deletar categoria: ${result.error}`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
            }
        } catch (error) {
            setDependencyError('Erro ao deletar categoria. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, hasDependencies, deleteDocument, filteredAndSortedCategories, currentPage]);

    // Validação numérica dos markups
    const validateMarkups = useCallback((padrao, minimo) => {
        const padraoNum = Number(padrao);
        const minimoNum = Number(minimo);

        if (isNaN(padraoNum) || isNaN(minimoNum)) {
            return { valid: false, message: "Markup deve ser um número válido." };
        }

        if (padraoNum < 0 || minimoNum < 0) {
            return { valid: false, message: "Markup não pode ser negativo." };
        }

        if (minimoNum > padraoNum) {
            return { valid: false, message: "Markup mínimo não pode ser maior que o markup padrão." };
        }

        if (padraoNum > 1000 || minimoNum > 1000) {
            return { valid: false, message: "Markup não pode exceder 1000%." };
        }

        return { valid: true, message: "" };
    }, []);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim() || !markupPadrao.trim() || !markupMinimo.trim() || !order.trim()) {
            setFormError("Todos os campos são obrigatórios.");
            return;
        }

        const markupValidation = validateMarkups(markupPadrao, markupMinimo);
        if (!markupValidation.valid) {
            setFormError(markupValidation.message);
            return;
        }

        const data = {
            name: nameInput.trim(),
            markupPadrao: Number(markupPadrao),
            markupMinimo: Number(markupMinimo),
            order: Number(order)
        };

        if (isNaN(data.order)) {
            setFormError("Ordem deve ser um número inteiro.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingCategory, userEmail);
        const finalData = { ...data, ...timestamps };

        setFormLoading(true);

        try {
            let result;
            if (editingCategory) {
                result = await updateDocument(editingCategory.id, finalData);
            } else {
                result = await addDocument(finalData);
            }

            if (result.success) {
                setFormSuccess(editingCategory ? 'Categoria editada com sucesso!' : 'Categoria adicionada com sucesso!');
                setTimeout(() => {
                    resetForm();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingCategory ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, markupPadrao, markupMinimo, order, editingCategory, validateMarkups, addDocument, updateDocument, resetForm]);

    // Ordenação dos dados
    const handleSort = useCallback((field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }, [sortField, sortDirection]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // Seta de ordenação
    const getSortIcon = useCallback((field) => {
        if (sortField !== field) return ' ↕';
        return sortDirection === 'asc' ? ' ↑' : ' ↓';
    }, [sortField, sortDirection]);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingCategory ? `Editar: ${editingCategory.name}` : 'Nova Categoria'}
                subtitle={editingCategory ? `Editando: ${editingCategory.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingCategory ? '💾 Salvar Edição' : '✨ Adicionar Categoria'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome da Categoria <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        required
                        disabled={formLoading}
                        className={styles.input}
                        placeholder="Ex: Eletrônicos, Vestuário, Alimentos..."
                        autoFocus
                    />
                </div>

                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="markupPadrao">
                            Markup Padrão (%) <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="markupPadrao"
                            type="number"
                            value={markupPadrao}
                            onChange={(e) => setMarkupPadrao(e.target.value)}
                            step="0.01"
                            min="0"
                            max="1000"
                            disabled={formLoading}
                            className={styles.input}
                            placeholder="Ex: 100"
                        />
                        <small className={styles.fieldHint}>Margem de lucro padrão aplicada</small>
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="markupMinimo">
                            Markup Mínimo (%) <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="markupMinimo"
                            type="number"
                            value={markupMinimo}
                            onChange={(e) => setMarkupMinimo(e.target.value)}
                            step="0.01"
                            min="0"
                            max="1000"
                            disabled={formLoading}
                            className={styles.input}
                            placeholder="Ex: 50"
                        />
                        <small className={styles.fieldHint}>Margem mínima permitida</small>
                    </div>
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="order">
                        Ordem de Exibição <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="order"
                        type="number"
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                        disabled={formLoading}
                        className={styles.input}
                        placeholder="Número inteiro"
                        min="0"
                    />
                    <small className={styles.fieldHint}>Define a posição no menu (menor = primeiro)</small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja excluir a categoria "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            >
                {dependencyError && (
                    <div className={`${styles.alert} ${styles.error}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>⚠️</span>
                        {dependencyError}
                    </div>
                )}
            </ConfirmModal>

            {/* Page Header */}
            <PageHeader
                title="Categorias de Produto"
                actionText="Adicionar Categoria"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome, ordem ou markup..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar categorias: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />
                    ) : currentCategories.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma categoria cadastrada'
                            }
                            message={
                                searchTerm
                                    ? 'Tente outros termos ou limpe a busca.'
                                    : "Clique em 'Adicionar Categoria' para começar."
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th
                                                style={{ width: '80px', cursor: 'pointer' }}
                                                onClick={() => handleSort('order')}
                                            >
                                                Ordem{getSortIcon('order')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSort('name')}
                                            >
                                                Categoria de Produto{getSortIcon('name')}
                                            </th>
                                            <th
                                                style={{ width: '150px', cursor: 'pointer' }}
                                                onClick={() => handleSort('markupPadrao')}
                                            >
                                                Markup Padrão{getSortIcon('markupPadrao')}
                                            </th>
                                            <th
                                                style={{ width: '150px', cursor: 'pointer' }}
                                                onClick={() => handleSort('markupMinimo')}
                                            >
                                                Markup Mínimo{getSortIcon('markupMinimo')}
                                            </th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentCategories.map((category) => (
                                            <tr key={category.id} className={styles.tableRow}>
                                                <td>{category.order}</td>
                                                <td className={styles.conditionName}>
                                                    <strong>{category.name}</strong>
                                                </td>
                                                <td className={styles.daysBadge}>
                                                    <span className={styles.badge}>
                                                        {category.markupPadrao?.toFixed(2)}%
                                                    </span>
                                                </td>
                                                <td className={styles.daysBadge}>
                                                    <span className={styles.badge}>
                                                        {category.markupMinimo?.toFixed(2)}%
                                                    </span>
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(category)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(category.id, category.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="categorias"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ProductCategoryPage;